/**
 * lib/debtors.ts
 * ------------------------------------------------------------------
 * THE DEBTORS MASTER — one row per account that owes the school money.
 *
 * Pastel calls this Customers, and it is the screen a finance department opens
 * to answer the only question they ask about receivables all day: who owes what,
 * how old is it, and who do I ring. The pieces existed here — invoices,
 * statements, an aging report — but nothing put them on one line per account,
 * which is why "give me the debtors listing" had no answer.
 *
 * WHO IS THE CUSTOMER? Not the pupil. Fees are billed per pupil but PAID by a
 * family, statements are family-scoped, and chasing a seven-year-old for arrears
 * is not a thing. So the account is the FAMILY, with the pupils shown as the
 * detail behind it — and a pupil with no family on record becomes an account in
 * their own right rather than disappearing from the listing, which is the
 * failure mode that matters: a debtor nobody can see is a debt nobody collects.
 *
 * BALANCES ARE COMPUTED FROM THE INVOICES, never stored on the family. A stored
 * customer balance is the classic thing that drifts from the ledger and takes a
 * fortnight to explain.
 *
 * PURE. No Firestore, no React.
 * ------------------------------------------------------------------ */
import { ageInvoices, daysPastDue, type Aging, type Invoice } from '@/lib/financeCore';

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

export interface DebtorPupil {
  id: string;
  name: string;
  className?: string;
  /** The class ID, not just its display name — the daily register is keyed on
   *  it, and the fees office needs to read that register before chasing. */
  classId?: string;
  /** Roll status: 'left' where the pupil has withdrawn. A debtor who has left is
   *  a different decision from one who is still in class, and the ledger alone
   *  cannot tell them apart. */
  status?: string;
  leftDate?: string;
  billed: number;
  paid: number;
  balance: number;
}

export interface DebtorAccount {
  /** Family id, or the pupil's own id where there is no family. */
  id: string;
  /** True when this account exists only because the pupil has no family. */
  unlinked: boolean;
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
  pupils: DebtorPupil[];
  /** Classes the pupils sit in — how a bursar recognises a family. */
  classes: string[];
  billed: number;
  paid: number;
  /** Positive is owed to the school; negative is held on account. */
  balance: number;
  /** Age of the oldest unpaid invoice, in days. 0 when nothing is overdue. */
  oldestDays: number;
  aging: Aging;
  invoiceCount: number;
  /** When the school last received anything from this account. */
  lastPaymentAt?: string;
  lastPaymentAmount?: number;
  status: DebtorStatus;
}

/**
 * `settled` and `never billed` are separated deliberately. Both show nil
 * outstanding, and they mean opposite things: one family has paid everything
 * asked of them, the other has never been invoiced — which is usually a billing
 * error, and is invisible if the two are lumped together.
 */
export type DebtorStatus = 'owing' | 'overdue' | 'settled' | 'in-credit' | 'not-billed';

export const DEBTOR_STATUS_LABEL: Record<DebtorStatus, string> = {
  overdue: 'Overdue',
  owing: 'Owing',
  settled: 'Settled',
  'in-credit': 'In credit',
  'not-billed': 'Never billed',
};

export interface DebtorSources {
  invoices: Invoice[];
  payments: { studentId?: string; invoiceId?: string; amount?: number; receivedAt?: string; createdAt?: string }[];
  students: { id: string; fullName: string; familyId?: string; currentClass?: string; status?: string; leftDate?: string; parentContact?: unknown }[];
  families: { id: string; name?: string; studentIds?: string[]; primaryContact?: { name?: string; email?: string; whatsapp?: string; phone?: string } }[];
  classNameOf?: (classId: string) => string | undefined;
  /** Aging thresholds, from finance settings. */
  thresholds?: number[];
  asOf?: Date;
}

export function buildDebtors(src: DebtorSources): DebtorAccount[] {
  const asOf = src.asOf ?? new Date();
  const thresholds = src.thresholds?.length ? src.thresholds : [30, 60, 90];

  const studentById = new Map(src.students.map((s) => [s.id, s]));
  const familyById = new Map(src.families.map((f) => [f.id, f]));

  /** Which account a pupil belongs to — their family, or themselves. */
  const accountOf = (studentId: string): { id: string; unlinked: boolean } => {
    const fam = studentById.get(studentId)?.familyId;
    return fam && familyById.has(fam)
      ? { id: fam, unlinked: false }
      : { id: studentId, unlinked: true };
  };

  const live = src.invoices.filter((i) => i.status !== 'void' && i.status !== 'draft');

  const byAccount = new Map<string, { unlinked: boolean; invoices: Invoice[]; pupilIds: Set<string> }>();
  live.forEach((inv) => {
    const { id, unlinked } = accountOf(inv.studentId);
    const cur = byAccount.get(id) ?? { unlinked, invoices: [], pupilIds: new Set<string>() };
    cur.invoices.push(inv);
    cur.pupilIds.add(inv.studentId);
    byAccount.set(id, cur);
  });

  // Every pupil on the roll gets an account even with nothing billed — a family
  // that has never been invoiced is exactly what a debtors listing should show.
  src.students.forEach((s) => {
    const { id, unlinked } = accountOf(s.id);
    const cur = byAccount.get(id) ?? { unlinked, invoices: [], pupilIds: new Set<string>() };
    cur.pupilIds.add(s.id);
    byAccount.set(id, cur);
  });

  const lastPayment = new Map<string, { at: string; amount: number }>();
  src.payments.forEach((p) => {
    if (!p.studentId) return;
    const { id } = accountOf(p.studentId);
    const at = p.receivedAt || p.createdAt || '';
    const prev = lastPayment.get(id);
    if (!prev || at > prev.at) lastPayment.set(id, { at, amount: Number(p.amount) || 0 });
  });

  const out: DebtorAccount[] = [];

  byAccount.forEach((entry, id) => {
    const family = familyById.get(id);
    const pupils: DebtorPupil[] = [...entry.pupilIds].map((sid) => {
      const s = studentById.get(sid);
      const theirs = entry.invoices.filter((i) => i.studentId === sid);
      const billed = round2(theirs.reduce((n, i) => n + (Number(i.total) || 0), 0));
      const paid = round2(theirs.reduce((n, i) => n + (Number(i.paid) || 0), 0));
      return {
        id: sid,
        name: s?.fullName ?? theirs[0]?.studentName ?? sid,
        className: s?.currentClass ? (src.classNameOf?.(s.currentClass) ?? s.currentClass) : undefined,
        classId: s?.currentClass,
        status: s?.status,
        leftDate: s?.leftDate,
        billed,
        paid,
        balance: round2(theirs.reduce((n, i) => n + (Number(i.balance) || 0), 0)),
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    const billed = round2(pupils.reduce((n, p) => n + p.billed, 0));
    const paid = round2(pupils.reduce((n, p) => n + p.paid, 0));
    const balance = round2(pupils.reduce((n, p) => n + p.balance, 0));

    const unpaid = entry.invoices.filter((i) => (Number(i.balance) || 0) > 0);
    const oldestDays = unpaid.reduce(
      (n, i) => Math.max(n, Math.max(0, daysPastDue(i.dueDate, asOf))), 0,
    );

    const contact = family?.primaryContact;
    const status: DebtorStatus = balance < -0.005
      ? 'in-credit'
      : entry.invoices.length === 0
        ? 'not-billed'
        : balance < 0.005
          ? 'settled'
          : oldestDays > 0 ? 'overdue' : 'owing';

    const last = lastPayment.get(id);

    out.push({
      id,
      unlinked: entry.unlinked,
      name: family?.name
        || (entry.unlinked ? `${pupils[0]?.name ?? id} (no family linked)` : id),
      contactName: contact?.name,
      phone: contact?.whatsapp || contact?.phone,
      email: contact?.email,
      pupils,
      classes: [...new Set(pupils.map((p) => p.className).filter(Boolean) as string[])].sort(),
      billed,
      paid,
      balance,
      oldestDays,
      aging: ageInvoices(entry.invoices, thresholds, asOf),
      invoiceCount: entry.invoices.length,
      lastPaymentAt: last?.at,
      lastPaymentAmount: last?.amount,
      status,
    });
  });

  // Biggest debt first — a listing sorted alphabetically is a listing nobody
  // works from.
  return out.sort((a, b) => b.balance - a.balance || a.name.localeCompare(b.name));
}

export interface DebtorTotals {
  accounts: number;
  billed: number;
  paid: number;
  outstanding: number;
  inCredit: number;
  overdueAccounts: number;
  notBilled: number;
  /** Collected as a share of billed. */
  collectionRate: number;
}

export function debtorTotals(rows: DebtorAccount[]): DebtorTotals {
  const billed = round2(rows.reduce((n, r) => n + r.billed, 0));
  const paid = round2(rows.reduce((n, r) => n + r.paid, 0));
  return {
    accounts: rows.length,
    billed,
    paid,
    outstanding: round2(rows.filter((r) => r.balance > 0).reduce((n, r) => n + r.balance, 0)),
    inCredit: round2(Math.abs(rows.filter((r) => r.balance < 0).reduce((n, r) => n + r.balance, 0))),
    overdueAccounts: rows.filter((r) => r.status === 'overdue').length,
    notBilled: rows.filter((r) => r.status === 'not-billed').length,
    collectionRate: billed > 0 ? Math.round((paid / billed) * 1000) / 10 : 0,
  };
}

/** The listing as CSV — Pastel's customer listing, in the form a finance
 *  department will immediately try to open in a spreadsheet. */
export function debtorsCsv(rows: DebtorAccount[]): string {
  const head = [
    'Account', 'Name', 'Contact', 'Phone', 'Email', 'Pupils', 'Classes',
    'Billed', 'Paid', 'Balance', 'Oldest days', 'Status', 'Last payment',
  ];
  const esc = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) => [
    r.id, r.name, r.contactName ?? '', r.phone ?? '', r.email ?? '',
    r.pupils.map((p) => p.name).join('; '), r.classes.join('; '),
    r.billed.toFixed(2), r.paid.toFixed(2), r.balance.toFixed(2),
    r.oldestDays || '', DEBTOR_STATUS_LABEL[r.status],
    r.lastPaymentAt ? r.lastPaymentAt.slice(0, 10) : '',
  ].map(esc).join(','));
  return [head.join(','), ...body].join('\n');
}
